import { apiClient } from './api'
import type { ServiceDocument, ServiceListItem } from '@/types/services'
import { ServiceDocumentType } from '@/types/services'

// Export types for use in components
export type { ServiceDocument, ServiceListItem }

// Enums
export enum ServiceStatus {
  Draft = 'Draft',
  Active = 'Active',
  Inactive = 'Inactive',
  Archived = 'Archived',
  UnderReview = 'UnderReview',
  Rejected = 'Rejected'
}

// ServiceVisibility removed - all services are publicly readable

export enum AvailabilityType {
  Regular = 'Regular',
  Exception = 'Exception',
  Holiday = 'Holiday',
  Vacation = 'Vacation'
}

// Flags enum for configuring which recurrence types a service allows
// Values match the backend enum for bitwise operations
export enum RecurringBookingType {
  None = 0,
  Daily = 1,      // Customer can pick multiple specific days
  Weekly = 2,     // Same day each week
  Monthly = 4,    // Same date each month
  All = 7         // Daily | Weekly | Monthly
}

// Types
export interface ServiceDetails {
  id: string
  name: string
  serviceCode?: string
  description: string
  shortDescription?: string
  providerId: string
  providerName: string
  categoryId: string
  categoryName: string
  price: number
  durationMinutes: number
  isActive: boolean
  requiresApproval: boolean
  requiresDocumentUpload?: boolean
  maxAdvanceBookingDays?: number
  minAdvanceBookingMinutes?: number
  requiresUpfrontPayment?: boolean
  bookingFeeAmount?: number
  cancellationHoursBeforeAppointment?: number | null
  amendmentHoursBeforeAppointment?: number | null
  preprationInstructions?: string
  afterCareInstructions?: string
  cancellationPolicy?: string
  status: ServiceStatus
  // visibility removed - all services are publicly readable
  createdAt: string
  updatedAt: string
  averageRating: number
  reviewCount: number
  logoImageUrl?: string
  images: ServiceImage[]
  // Advanced availability
  useAdvancedAvailability?: boolean
  // Recurring appointment configuration
  allowRecurringBookings?: boolean
  allowedRecurrenceTypes?: number // RecurringBookingType flags
  maxRecurringAppointments?: number
  locations: ServiceLocation[]
  availabilities: ServiceAvailability[]
}

export interface ServiceImage {
  id: string
  imageUrl: string
  altText?: string
  isPrimary: boolean
  sortOrder: number
  /** True when this image is the service's downloadable flyer. */
  isFlyer?: boolean
}

export interface ServiceLocation {
  id: string
  locationId: string
  locationName: string
  locationAddress: string
  isDefault: boolean
}

export interface ServiceAvailability {
  id: string
  dayOfWeek: number
  startTime: string
  endTime: string
  isAvailable: boolean
  capacity: number
  effectiveFrom?: string
  effectiveTo?: string
  type: AvailabilityType
}

export interface ServiceCategory {
  id: string
  name: string
  description: string
  icon?: string
  color?: string
  parentId?: string
  parentName?: string
  isActive: boolean
  sortOrder: number
  servicesCount: number
  children: ServiceCategory[]
  createdAt?: string
  updatedAt?: string
}

export interface Location {
  id: string
  name: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  postalCode: string
  country: string
  latitude?: number
  longitude?: number
  phoneNumber?: string
  email?: string
  description?: string
  timeZoneId?: string
  timeZoneDisplayName?: string
  isActive: boolean
  providerId: string
  providerName: string
  fullAddress: string
  serviceLocations?: LocationService[]
}

export interface LocationService {
  serviceId: string
  serviceName: string
}

export interface ServiceReview {
  id: string
  serviceId: string
  clientId: string
  clientName: string
  rating: number
  comment?: string
  isVerified: boolean
  isVisible: boolean
  reviewedAt: string
}

export interface ServiceSearchResponse {
  services: ServiceListItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  categories: ServiceCategory[]
  minPrice: number
  maxPrice: number
}

export interface ServiceSearchParams {
  searchTerm?: string
  categoryId?: string
  categoryIds?: string[]  // Support multiple category filtering
  providerId?: string
  minPrice?: number
  maxPrice?: number
  status?: ServiceStatus
  // visibility removed - all services are publicly readable
  isActive?: boolean
  locationId?: string
  locationSearchTerm?: string  // For text-based location search (city, postcode, address)
  latitude?: number
  longitude?: number
  radiusKm?: number
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: string
}

export interface CreateServiceRequest {
  name: string
  serviceCode?: string
  description: string
  shortDescription?: string
  categoryId: string
  price: number
  durationMinutes: number
  requiresApproval?: boolean
  requiresAgeVerification?: boolean
  requiresDocumentUpload?: boolean
  maxAdvanceBookingDays?: number
  minAdvanceBookingMinutes?: number
  requiresUpfrontPayment?: boolean
  bookingFeeAmount?: number
  requiresServiceNotes?: boolean
  serviceNotesPlaceholder?: string | null
  cancellationHoursBeforeAppointment?: number | null
  amendmentHoursBeforeAppointment?: number | null
  preparationInstructions?: string
  afterCareInstructions?: string
  cancellationPolicy?: string
  logoImageUrl?: string
  // visibility removed - all services are publicly readable
  locationId: string | null
  availabilities: CreateServiceAvailabilityRequest[]
  // Recurring appointment configuration
  allowRecurringBookings?: boolean
  allowedRecurrenceTypes?: number // RecurringBookingType flags
  maxRecurringAppointments?: number
}

export interface UpdateServiceRequest {
  name: string
  serviceCode?: string
  description: string
  shortDescription?: string
  categoryId: string
  price: number
  durationMinutes: number
  requiresApproval?: boolean
  requiresAgeVerification?: boolean
  requiresDocumentUpload?: boolean
  maxAdvanceBookingDays?: number
  minAdvanceBookingMinutes?: number
  requiresUpfrontPayment?: boolean
  bookingFeeAmount?: number
  requiresServiceNotes?: boolean
  serviceNotesPlaceholder?: string | null
  cancellationHoursBeforeAppointment?: number | null
  amendmentHoursBeforeAppointment?: number | null
  preparationInstructions?: string
  afterCareInstructions?: string
  cancellationPolicy?: string
  logoImageUrl?: string
  // visibility removed - all services are publicly readable
  // Recurring appointment configuration
  allowRecurringBookings?: boolean
  allowedRecurrenceTypes?: number // RecurringBookingType flags
  maxRecurringAppointments?: number
}

export interface CreateServiceAvailabilityRequest {
  dayOfWeek: number
  startTime: string
  endTime: string
  capacity?: number
  isAvailable?: boolean
  effectiveFrom?: string
  effectiveTo?: string
  type?: AvailabilityType
}

export interface UpdateServiceAvailabilityRequest extends CreateServiceAvailabilityRequest {
  id?: string
}

export interface AddServiceImageRequest {
  imageUrl: string
  altText?: string
  isPrimary?: boolean
  sortOrder?: number
}

export interface CreateLocationRequest {
  name: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  postalCode: string
  country: string
  latitude?: number
  longitude?: number
  phoneNumber?: string
  email?: string
  description?: string
  timeZoneId?: string
  isActive?: boolean
}

export interface UpdateLocationRequest {
  name: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  postalCode: string
  country: string
  latitude?: number
  longitude?: number
  phoneNumber?: string
  email?: string
  description?: string
  timeZoneId?: string
  isActive: boolean
}

// Helper function to transform ServiceDocument to ServiceImage
function transformDocumentToImage(document: ServiceDocument): ServiceImage {
  return {
    id: document.id,
    imageUrl: document.documentUrl,
    altText: document.altText,
    isPrimary: document.isPrimary,
    sortOrder: document.sortOrder,
    isFlyer: document.isFlyer
  }
}

// Helper function to filter and transform image documents
function getImagesFromDocuments(documents: ServiceDocument[]): ServiceImage[] {
  return documents
    .filter(doc => 
      doc.isPublic && (
        doc.documentType === ServiceDocumentType.Image ||
        doc.documentType === ServiceDocumentType.Logo ||
        doc.documentType === ServiceDocumentType.Thumbnail ||
        doc.documentType === ServiceDocumentType.Banner
      )
    )
    .sort((a, b) => {
      // Primary images first, then by sort order
      if (a.isPrimary && !b.isPrimary) return -1
      if (!a.isPrimary && b.isPrimary) return 1
      return a.sortOrder - b.sortOrder
    })
    .map(transformDocumentToImage)
}

export class ServiceService {

  // Search services (for authenticated providers - shows all their services)
  static async searchServices(params: ServiceSearchParams = {}): Promise<ServiceSearchResponse> {
    return apiClient.get<ServiceSearchResponse>('/services', {
      params
    })
  }

  // Search public services (for public listing pages - only returns Active services)
  static async searchPublicServices(params: ServiceSearchParams = {}): Promise<ServiceSearchResponse> {
    return apiClient.get<ServiceSearchResponse>('/public/services', {
      params
    })
  }

  // Get service by ID (authenticated - shows any service)
  static async getServiceById(id: string): Promise<ServiceDetails> {
    const response = await apiClient.get<any>(`/services/${id}`)
    // Transform documents to images for frontend compatibility
    return {
      ...response,
      images: response.documents ? getImagesFromDocuments(response.documents) : []
    }
  }

  // Get public service by ID (only returns Active services)
  static async getPublicServiceById(id: string): Promise<ServiceDetails> {
    const response = await apiClient.get<any>(`/public/services/${id}`)
    // Transform documents to images for frontend compatibility
    return {
      ...response,
      images: response.documents ? getImagesFromDocuments(response.documents) : []
    }
  }

  // Get public service by service code (only returns Active services)
  static async getPublicServiceByCode(serviceCode: string): Promise<ServiceDetails> {
    const response = await apiClient.get<any>(`/public/services/code/${serviceCode}`)
    // Transform documents to images for frontend compatibility
    return {
      ...response,
      images: response.documents ? getImagesFromDocuments(response.documents) : []
    }
  }

  // Helper to detect if a string is a UUID
  static isUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(str)
  }

  // Get service by ID or code - auto-detects and calls appropriate method
  static async getServiceByIdOrCode(idOrCode: string): Promise<ServiceDetails> {
    if (this.isUUID(idOrCode)) {
      return this.getServiceById(idOrCode)
    } else {
      return this.getPublicServiceByCode(idOrCode)
    }
  }

  // Get services by provider
  static async getServicesByProvider(providerId: string, isActive?: boolean): Promise<ServiceListItem[]> {
    return apiClient.get<ServiceListItem[]>(`/services/provider/${providerId}`, {
      params: { isActive }
    })
  }

  // Get services by category
  static async getServicesByCategory(categoryId: string, isActive?: boolean): Promise<ServiceListItem[]> {
    return apiClient.get<ServiceListItem[]>(`/services/category/${categoryId}`, {
      params: { isActive }
    })
  }

  // Get featured services (authenticated)
  static async getFeaturedServices(count: number = 10): Promise<ServiceListItem[]> {
    return apiClient.get<ServiceListItem[]>('/services/featured', {
      params: { count }
    })
  }

  // Get public featured services (only returns Active services)
  static async getPublicFeaturedServices(count: number = 10): Promise<ServiceListItem[]> {
    return apiClient.get<ServiceListItem[]>('/public/services/featured', {
      params: { count }
    })
  }

  // Create service
  static async createService(serviceData: CreateServiceRequest): Promise<ServiceDetails> {
    return apiClient.post<ServiceDetails>('/services', serviceData)
  }

  // Update service
  static async updateService(id: string, serviceData: UpdateServiceRequest): Promise<ServiceDetails> {
    return apiClient.put<ServiceDetails>(`/services/${id}`, serviceData)
  }

  // Update service availability
  static async updateServiceAvailability(id: string, availabilities: UpdateServiceAvailabilityRequest[]): Promise<ServiceDetails> {
    return apiClient.put<ServiceDetails>(`/services/${id}/availability`, availabilities)
  }

  // Update service location
  static async updateServiceLocation(id: string, locationId: string | null): Promise<ServiceDetails> {
    return apiClient.put<ServiceDetails>(`/services/${id}/location`, { locationId })
  }

  // Toggle service status
  static async toggleServiceStatus(id: string, activate: boolean): Promise<ServiceDetails> {
    return apiClient.post<ServiceDetails>(`/services/${id}/toggle-status`, activate)
  }

  // Publish service
  static async publishService(id: string): Promise<ServiceDetails> {
    return apiClient.post<ServiceDetails>(`/services/${id}/publish`)
  }

  // Archive service
  static async archiveService(id: string): Promise<ServiceDetails> {
    return apiClient.post<ServiceDetails>(`/services/${id}/archive`)
  }

  // Add service image
  static async addServiceImage(id: string, imageData: AddServiceImageRequest): Promise<ServiceImage> {
    return apiClient.post<ServiceImage>(`/services/${id}/images`, imageData)
  }

  // Remove service image
  static async removeServiceImage(id: string, imageId: string): Promise<void> {
    return apiClient.delete<void>(`/services/${id}/images/${imageId}`)
  }

  // Delete service
  static async deleteService(id: string): Promise<void> {
    return apiClient.delete<void>(`/services/${id}`)
  }

  // Get service reviews
  static async getServiceReviews(
    id: string, 
    isVisible: boolean = true, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ServiceReview[]> {
    return apiClient.get<ServiceReview[]>(`/services/${id}/reviews`, {
      params: { isVisible, page, pageSize }
    })
  }

  // Get service categories (authenticated)
  static async getServiceCategories(isActive?: boolean, includeChildren: boolean = true): Promise<ServiceCategory[]> {
    return apiClient.get<ServiceCategory[]>('/servicecategories', {
      params: {
        isActive,
        includeChildren
      }
    })
  }

  // Get public service categories (only returns categories with active services)
  static async getPublicServiceCategories(): Promise<ServiceCategory[]> {
    return apiClient.get<ServiceCategory[]>('/public/services/categories')
  }

  // Get category by ID
  static async getServiceCategoryById(id: string, includeChildren: boolean = true): Promise<ServiceCategory> {
    return apiClient.get<ServiceCategory>(`/servicecategories/${id}`, {
      params: { includeChildren }
    })
  }

  // Create service category
  static async createServiceCategory(categoryData: {
    name: string
    description: string
    icon?: string
    color?: string
    parentId?: string
    sortOrder: number
    isActive: boolean
  }): Promise<ServiceCategory> {
    return apiClient.post<ServiceCategory>('/servicecategories', categoryData)
  }

  // Update service category
  static async updateServiceCategory(id: string, categoryData: {
    name: string
    description: string
    icon?: string
    color?: string
    parentId?: string
    sortOrder: number
    isActive: boolean
  }): Promise<ServiceCategory> {
    return apiClient.put<ServiceCategory>(`/servicecategories/${id}`, categoryData)
  }

  // Delete service category
  static async deleteServiceCategory(id: string, forceDelete: boolean = false): Promise<void> {
    return apiClient.delete(`/servicecategories/${id}`, {
      params: { forceDelete }
    })
  }

  // Get locations by provider
  static async getLocationsByProvider(providerId: string, isActive?: boolean): Promise<Location[]> {
    return apiClient.get<Location[]>(`/locations/provider/${providerId}`, {
      params: { isActive }
    })
  }

  // Get my locations (current provider)
  static async getMyLocations(isActive?: boolean): Promise<Location[]> {
    return apiClient.get<Location[]>('/locations/my', {
      params: { isActive }
    })
  }

  // Get location by ID
  static async getLocationById(id: string): Promise<Location> {
    return apiClient.get<Location>(`/locations/${id}`)
  }

  // Create location
  static async createLocation(locationData: CreateLocationRequest): Promise<Location> {
    return apiClient.post<Location>('/locations', locationData)
  }

  // Update location
  static async updateLocation(id: string, locationData: UpdateLocationRequest): Promise<Location> {
    return apiClient.put<Location>(`/locations/${id}`, locationData)
  }

  // Delete location
  static async deleteLocation(id: string): Promise<void> {
    return apiClient.delete(`/locations/${id}`)
  }

  // Toggle location status
  static async toggleLocationStatus(id: string, activate: boolean): Promise<Location> {
    return apiClient.post<Location>(`/locations/${id}/toggle-status`, activate)
  }

  // Get provider availability for a service on a specific date
  static async getProviderAvailability(
    serviceId: string,
    date: string,
    currentAppointmentStartTime?: string
  ): Promise<Array<{time: string, available: boolean, isCurrentAppointment?: boolean, unavailableReason?: string}>> {
    interface TimeSlotDto {
      startTime: string
      startTimeLocal: string
      isAvailable: boolean
      isCurrentAppointment?: boolean
      unavailableReason?: string
    }

    const params: Record<string, string> = { date }
    if (currentAppointmentStartTime) {
      params.currentAppointmentStartTime = currentAppointmentStartTime
    }

    const response = await apiClient.get<TimeSlotDto[]>(`/appointments/services/${serviceId}/available-slots`, {
      params
    })

    // Transform backend response to expected format
    return response.map(slot => {
      // Extract time from startTimeLocal (format: "2024-01-15T09:00:00")
      // wall-clock from BE — do not re-shift via useFormatDate composable.
      const localTime = slot.startTimeLocal ? new Date(slot.startTimeLocal) : new Date(slot.startTime)
      const time = localTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })

      return {
        time,
        available: slot.isAvailable,
        isCurrentAppointment: slot.isCurrentAppointment,
        unavailableReason: slot.unavailableReason
      }
    })
  }

  // Upload service logo
  static async uploadServiceLogo(serviceId: string, file: File): Promise<{ logoUrl: string }> {
    const formData = new FormData()
    formData.append('logoFile', file)

    return apiClient.post<{ logoUrl: string }>(`/services/${serviceId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Remove service logo
  static async removeServiceLogo(serviceId: string): Promise<void> {
    return apiClient.delete<void>(`/services/${serviceId}/logo`)
  }

  // Upload service image
  static async uploadServiceImage(
    serviceId: string,
    file: File,
    metadata?: {
      title?: string
      description?: string
      altText?: string
      isPrimary?: boolean
      sortOrder?: number
    }
  ): Promise<ServiceDocument> {
    const formData = new FormData()
    formData.append('imageFile', file)

    if (metadata?.title) formData.append('title', metadata.title)
    if (metadata?.description) formData.append('description', metadata.description)
    if (metadata?.altText) formData.append('altText', metadata.altText)
    if (metadata?.isPrimary !== undefined) formData.append('isPrimary', metadata.isPrimary.toString())
    if (metadata?.sortOrder !== undefined) formData.append('sortOrder', metadata.sortOrder.toString())

    return apiClient.post<ServiceDocument>(`/services/${serviceId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Set primary image
  static async setPrimaryImage(serviceId: string, imageId: string): Promise<void> {
    return apiClient.post<void>(`/services/${serviceId}/images/${imageId}/set-primary`)
  }

  // Upload service document
  static async uploadServiceDocument(
    serviceId: string,
    file: File,
    metadata?: {
      documentType?: ServiceDocumentType
      title?: string
      description?: string
      isPublic?: boolean
    }
  ): Promise<ServiceDocument> {
    const formData = new FormData()
    formData.append('documentFile', file)

    if (metadata?.documentType !== undefined) formData.append('documentType', metadata.documentType.toString())
    if (metadata?.title) formData.append('title', metadata.title)
    if (metadata?.description) formData.append('description', metadata.description)
    if (metadata?.isPublic !== undefined) formData.append('isPublic', metadata.isPublic.toString())

    return apiClient.post<ServiceDocument>(`/services/${serviceId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Remove service document
  static async removeServiceDocument(serviceId: string, documentId: string): Promise<void> {
    return apiClient.delete<void>(`/services/${serviceId}/documents/${documentId}`)
  }

  /**
   * Provider: upload a NEW image and immediately mark it as the service's
   * flyer. Uses the dedicated 15 MB endpoint — the regular service image
   * upload cap is unchanged. Server atomically unmarks any prior flyer.
   */
  static async uploadServiceFlyer(
    serviceId: string,
    file: File,
    altText?: string
  ): Promise<ServiceDocument> {
    const formData = new FormData()
    formData.append('imageFile', file)
    if (altText) formData.append('altText', altText)
    return apiClient.post<ServiceDocument>(
      `/services/${serviceId}/flyer/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  }

  /** Client-side flyer size guard mirroring the server's 15 MB cap. */
  static isValidFlyerSize(file: File): boolean {
    return file.size <= 15 * 1024 * 1024
  }

  /**
   * Provider: mark or clear this document as the service's downloadable flyer.
   * Atomic on the server — any prior flyer on this service is auto-unmarked.
   * Only image-type documents may be flyers.
   */
  static async setServiceDocumentFlyer(
    serviceId: string,
    documentId: string,
    isFlyer: boolean
  ): Promise<ServiceDocument> {
    return apiClient.patch<ServiceDocument>(
      `/services/${serviceId}/documents/${documentId}/flyer`,
      { isFlyer }
    )
  }

  /**
   * Public: fetch a service's currently-marked flyer by service ID (or null).
   */
  static async getPublicServiceFlyerById(
    serviceId: string
  ): Promise<{ imageUrl: string; fileName: string; altText?: string } | null> {
    try {
      return await apiClient.get<{ imageUrl: string; fileName: string; altText?: string }>(
        `/public/services/${serviceId}/flyer`
      )
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) return null
      throw err
    }
  }

  /**
   * Public: fetch a service's currently-marked flyer by service code (or null).
   */
  static async getPublicServiceFlyerByCode(
    serviceCode: string
  ): Promise<{ imageUrl: string; fileName: string; altText?: string } | null> {
    try {
      return await apiClient.get<{ imageUrl: string; fileName: string; altText?: string }>(
        `/public/services/code/${serviceCode}/flyer`
      )
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) return null
      throw err
    }
  }

  // Get service documents
  static async getServiceDocuments(serviceId: string): Promise<ServiceDocument[]> {
    return apiClient.get<ServiceDocument[]>(`/services/${serviceId}/documents`)
  }

  // Add service tag
  static async addServiceTag(serviceId: string, tagData: { tagName: string; tagColor: string; sortOrder?: number }): Promise<any> {
    return apiClient.post<any>(`/services/${serviceId}/tags`, tagData)
  }

  // Remove service tag
  static async removeServiceTag(serviceId: string, tagId: string): Promise<void> {
    return apiClient.delete<void>(`/services/${serviceId}/tags/${tagId}`)
  }

  // Update service tag
  static async updateServiceTag(serviceId: string, tagId: string, tagData: { tagName: string; tagColor: string; sortOrder?: number }): Promise<any> {
    return apiClient.put<any>(`/services/${serviceId}/tags/${tagId}`, tagData)
  }

  // ===== Advanced Availability Methods =====

  // Get service calendar (includes weekly rules, date-specific overrides, and appointment counts)
  static async getServiceCalendar(
    serviceId: string,
    startDate: string,
    endDate: string
  ): Promise<ServiceCalendarResponse> {
    return apiClient.get<ServiceCalendarResponse>(`/services/${serviceId}/calendar`, {
      params: { startDate, endDate }
    })
  }

  // Toggle advanced availability mode
  static async toggleAdvancedAvailability(
    serviceId: string,
    enabled: boolean
  ): Promise<ServiceDetails> {
    return apiClient.put<ServiceDetails>(`/services/${serviceId}/advanced-availability`, { enabled })
  }

  // Set date-specific availability override
  static async setDateSpecificAvailability(
    serviceId: string,
    request: SetDateSpecificAvailabilityRequest
  ): Promise<DateSpecificAvailabilityDto> {
    return apiClient.post<DateSpecificAvailabilityDto>(
      `/services/${serviceId}/availability/date-specific`,
      request
    )
  }

  // Remove date-specific availability override
  static async removeDateSpecificAvailability(
    serviceId: string,
    date: string
  ): Promise<void> {
    return apiClient.delete<void>(`/services/${serviceId}/availability/date-specific/${date}`)
  }

  // Bulk set date-specific availability overrides
  static async bulkSetDateSpecificAvailability(
    serviceId: string,
    overrides: SetDateSpecificAvailabilityRequest[]
  ): Promise<DateSpecificAvailabilityDto[]> {
    return apiClient.post<DateSpecificAvailabilityDto[]>(
      `/services/${serviceId}/availability/date-specific/bulk`,
      { overrides }
    )
  }
}

// ===== Types for Advanced Availability =====

export interface DateSpecificAvailabilityDto {
  id: string
  serviceId: string
  specificDate: string
  capacity: number | null
  isBlocked: boolean
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface SetDateSpecificAvailabilityRequest {
  date: string
  capacity?: number | null
  isBlocked?: boolean
  notes?: string | null
}

export interface CalendarDayDto {
  date: string
  dayOfWeek: number
  isWorkingDay: boolean
  baseCapacity: number | null
  overrideCapacity: number | null
  effectiveCapacity: number
  isBlocked: boolean
  hasOverride: boolean
  bookedCount: number
  notes: string | null
}

export interface ServiceCalendarResponse {
  serviceId: string
  serviceName: string
  useAdvancedAvailability: boolean
  weeklyRules: ServiceAvailability[]
  dateSpecificOverrides: DateSpecificAvailabilityDto[]
  calendarDays: CalendarDayDto[]
}

export default ServiceService